/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/render-on-client.tsx';
import { getAiPlannerEmbedReceiverId } from '@atlassian/ai-planner-embed-bridge/AiPlannerBridgeCommands';
import { aiPlannerReceiverIdAttribute } from './aiPlannerReceiverIdAttribute.tsx';
import {
	type AiPlannerEmbedContext,
	getAiPlannerOrigin,
	getAiPlannerUrl,
	getBifrostOverrideFromLocation,
} from './getAiPlannerUrl.tsx';
import { AI_PLANNER_IFRAME_HOST_ATTRIBUTE } from './modalProps.tsx';
import { useAiPlannerBridge } from './useAiPlannerBridge.tsx';

// If the iframe has not signalled that its document is ready within this
// window, we treat the load as failed and surface the error fallback.
const LOAD_TIMEOUT_MS = 15_000;

export type AiPlannerIframeStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type AiPlannerIframeClaim = {
	/** Element the shared iframe should be moved into while claimed. */
	target: HTMLElement;
	/**
	 * Called when the planner asks the host to perform shell-level back
	 * navigation (the user pressed the planner's in-app back button).
	 */
	onBack?: () => void;
	/**
	 * Called when the planner asks the host to dismiss/close the embed (the user
	 * pressed the planner's in-app close button) — e.g. close the modal.
	 */
	onClose?: () => void;
};

type AiPlannerIframeContextValue = {
	/** Current status of the shared planner iframe for the active request. */
	status: AiPlannerIframeStatus;
	/**
	 * Boot the persistent iframe (at the stable planner root URL) in the
	 * background without revealing it. Safe to call repeatedly — the SPA boots
	 * once and is then driven via the bridge. `planId` is sent ahead via the
	 * bridge so the planner can pre-navigate while still hidden.
	 */
	warm: (planId?: string) => void;
	/**
	 * Ask the planner to warm the full-plan cache for the given plan ids ahead of
	 * a likely navigation (e.g. recent plans, or a hovered plan row), so a later
	 * `claim`/`warm` for one of them renders instantly. Boots the iframe if it is
	 * not already mounted. Best-effort, deduped and non-blocking — safe to call
	 * generously (e.g. on every hover).
	 */
	prewarm: (planIds: readonly string[]) => void;
	/**
	 * Take ownership of the shared iframe, moving its DOM node into `target`, and
	 * ask the planner (over the bridge) to navigate to `planId` (or root) — the
	 * iframe is NEVER reloaded. Returns a release function that returns the
	 * iframe to its hidden host.
	 */
	claim: (claim: AiPlannerIframeClaim, planId?: string) => () => void;
	/** Force a fresh reload after a failure. */
	retry: () => void;
};

const AiPlannerIframeContext = createContext<AiPlannerIframeContextValue | null>(null);

type AiPlannerIframeProviderProps = {
	/** Tenant context used to build the planner embed URL. */
	embedContext: AiPlannerEmbedContext;
	children: ReactNode;
};

/**
 * Owns a single, persistent Planner `<iframe>` so it can be warmed before the
 * modal opens and then reused — the modal reveals this one iframe (by moving its
 * DOM node into the modal body) instead of mounting its own, so a warmed planner
 * opens instantly with no remount or re-download.
 *
 * Mirrors the planner's own `ConfluenceBridgeProvider` pattern: lazy first
 * mount on intent, kept hidden until claimed, and reused across claims.
 */
export const AiPlannerIframeProvider = ({
	embedContext,
	children,
}: AiPlannerIframeProviderProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hostRef = useRef<HTMLDivElement | null>(null);
	const iframeRef = useRef<HTMLIFrameElement | null>(null);

	const [status, setStatus] = useState<AiPlannerIframeStatus>('idle');
	// Set once the iframe should exist in the DOM (after first warm/claim).
	const [isMounted, setIsMounted] = useState(false);
	// True while the iframe is revealed in a modal. Background warming is silent
	// (no overlay, no timeout); only a claimed/visible iframe drives status and
	// the load timeout so a slow background boot never poisons the warm.
	const [isClaimed, setIsClaimed] = useState(false);
	// Bumping this forces a remount of the iframe for a clean retry.
	const [loadKey, setLoadKey] = useState(0);
	const settledRef = useRef(false);

	// Stable root URL — the SPA boots ONCE at the planner root and never changes
	// its `src`. Plan navigation happens via the bridge (`sendLoadPlan`), so the
	// iframe is never reloaded when opening a different plan.
	const src = useMemo(
		// A planner Bifrost build can be pinned via the page URL's
		// `ai-planner-bifrost-override` query param (see getBifrostOverrideFromLocation).
		() => getAiPlannerUrl(embedContext, undefined, getBifrostOverrideFromLocation() ?? undefined),
		[embedContext],
	);

	// Tracks the requestId of the most recent loadPlan so we only react to the
	// planner's matching `ready` transition (ignoring stale/echoed ones).
	const pendingRequestIdRef = useRef<string | null>(null);

	// True once the iframe's root document (the SPA shell) has loaded at least
	// once. After that, the same persistent iframe is reused and won't fire
	// `onLoad` again, so claims rely on this + bridge transitions.
	const shellLoadedRef = useRef(false);

	// Back/close intents are owned by whoever currently has the iframe claimed
	// (the open modal). We hold the active claim's handlers in a ref so the bridge
	// client is created once and always calls the latest handlers.
	const claimIntentsRef = useRef<{ onBack?: () => void; onClose?: () => void }>({});

	const { sendLoadPlan, sendPrewarmPlans } = useAiPlannerBridge({
		onTransition: useCallback((transition) => {
			if (transition.requestId !== pendingRequestIdRef.current) {
				return;
			}
			if (transition.state === 'ready') {
				settledRef.current = true;
				setStatus('loaded');
			}
		}, []),
		onBack: useCallback(() => {
			claimIntentsRef.current.onBack?.();
		}, []),
		onClose: useCallback(() => {
			claimIntentsRef.current.onClose?.();
		}, []),
	});

	// Preconnect to the planner origin (only when cross-origin) so the TLS/DNS
	// connection is warm before the iframe loads. No-op when same-origin.
	useEffect(() => {
		const origin = getAiPlannerOrigin();
		if (origin == null || typeof globalThis.document === 'undefined') {
			return undefined;
		}
		const link = globalThis.document.createElement('link');
		link.rel = 'preconnect';
		link.href = origin;
		link.crossOrigin = '';
		globalThis.document.head.appendChild(link);
		return () => {
			globalThis.document.head.removeChild(link);
		};
	}, []);

	// Mount the persistent iframe once (boots the planner SPA at the root URL).
	// Stable identity: reads live state via refs, so it never re-creates and
	// never triggers claim/release churn.
	const bootRef = useRef(false);
	// Boot the persistent iframe once. This is SILENT — it never sets a visible
	// status or arms the timeout, so a slow background boot cannot error the
	// warm. The claimed/visible path owns status + timeout.
	const boot = useCallback(() => {
		if (bootRef.current) {
			return;
		}
		bootRef.current = true;
		setIsMounted(true);
	}, []);

	// Ask the planner to navigate (no reload). Records the requestId so we can
	// match the planner's `ready` transition. `visible` is true for a claim
	// (drives the overlay/timeout); false for background warming (stays silent).
	const requestPlan = useCallback(
		(nextPlanId: string | undefined, visible: boolean) => {
			const requestId = sendLoadPlan(nextPlanId);
			pendingRequestIdRef.current = requestId;
			if (!visible) {
				return;
			}
			// For a visible claim, show loading until ready — unless the shell has
			// already loaded AND we're opening the root (nothing to navigate to),
			// in which case it's already showing the right content.
			const isRoot = nextPlanId == null;
			if (shellLoadedRef.current && isRoot) {
				settledRef.current = true;
				setStatus('loaded');
				return;
			}
			settledRef.current = false;
			setStatus('loading');
		},
		[sendLoadPlan],
	);

	const warm = useCallback(
		(nextPlanId?: string) => {
			boot();
			requestPlan(nextPlanId, false);
		},
		[boot, requestPlan],
	);

	const prewarm = useCallback(
		(planIds: readonly string[]) => {
			// Boot the (hidden) iframe so the planner is alive to receive and act on
			// the prewarm; the warming itself is silent and never blocks.
			boot();
			sendPrewarmPlans(planIds);
		},
		[boot, sendPrewarmPlans],
	);

	// The modal slot the iframe is currently overlaying (null when parked).
	const [claimTarget, setClaimTarget] = useState<HTMLElement | null>(null);

	const claim = useCallback(
		({ target, onBack, onClose }: AiPlannerIframeClaim, nextPlanId?: string) => {
			boot();
			setIsClaimed(true);
			// Route the planner's back/close intents to this claimer (the open
			// modal) for as long as it holds the iframe.
			claimIntentsRef.current = { onBack, onClose };
			// IMPORTANT: we do NOT move the iframe DOM node — moving an <iframe>
			// between parents reloads it (and throws away the warm SPA). Instead we
			// keep it in its fixed host and visually overlay the modal slot.
			setClaimTarget(target);
			requestPlan(nextPlanId, true);
			return () => {
				setIsClaimed(false);
				setClaimTarget(null);
				claimIntentsRef.current = {};
			};
		},
		[boot, requestPlan],
	);

	// Position the (never-moved) iframe host over the claimed modal slot, keeping
	// it aligned across scroll/resize/layout changes via rAF. When unclaimed the
	// host collapses to a hidden, out-of-flow box.
	const [hostRect, setHostRect] = useState<{
		top: number;
		left: number;
		width: number;
		height: number;
	} | null>(null);

	useEffect(() => {
		if (claimTarget == null || typeof globalThis.window === 'undefined') {
			// Clearing the overlay geometry when parked; safe one-off reset.
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHostRect(null);
			return undefined;
		}
		let frame = 0;
		let last = '';
		const sync = () => {
			const rect = claimTarget.getBoundingClientRect();
			const key = `${rect.top}|${rect.left}|${rect.width}|${rect.height}`;
			if (key !== last) {
				last = key;
				// Syncing to an external source (the live slot geometry) via rAF —
				// this is a subscription callback, not a synchronous effect body.
				setHostRect({
					top: rect.top,
					left: rect.left,
					width: rect.width,
					height: rect.height,
				});
			}
			frame = globalThis.requestAnimationFrame(sync);
		};
		frame = globalThis.requestAnimationFrame(sync);
		return () => {
			globalThis.cancelAnimationFrame(frame);
		};
	}, [claimTarget]);

	const retry = useCallback(() => {
		settledRef.current = false;
		setStatus('loading');
		setLoadKey((key) => key + 1);
	}, []);

	const handleLoad = useCallback(() => {
		// The SPA shell has loaded — record it so later claims know the iframe is
		// already booted (it won't fire onLoad again once reused).
		shellLoadedRef.current = true;
		fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe loaded');
		// Only lift the overlay when the iframe is actually visible in a modal;
		// background warming stays silent. (A specific-plan claim still waits for
		// the bridge `ready` transition — but the shell load is a safe fallback.)
		if (settledRef.current) {
			return;
		}
		settledRef.current = true;
		setStatus((prev) => (prev === 'idle' ? prev : 'loaded'));
	}, [createAnalyticsEvent]);

	const handleError = useCallback(() => {
		if (settledRef.current) {
			return;
		}
		settledRef.current = true;
		// Only surface the error when visible; a failed background warm is silent
		// and will be retried/handled when the user actually opens the modal.
		setStatus((prev) => (prev === 'idle' ? prev : 'error'));
		fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe failed', {
			reason: 'error',
		});
	}, [createAnalyticsEvent]);

	// Treat a stalled load as a failure — but ONLY while the iframe is visible in
	// a modal. Background warming never times out, so a slow cold boot can't
	// poison the warm before the user even opens it.
	useEffect(() => {
		if (!isClaimed || status !== 'loading') {
			return undefined;
		}
		const timeoutId = setTimeout(() => {
			if (settledRef.current) {
				return;
			}
			settledRef.current = true;
			setStatus('error');
			fireOperationalAnalytics(createAnalyticsEvent({}), 'aiPlannerIframe failed', {
				reason: 'timeout',
			});
		}, LOAD_TIMEOUT_MS);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [isClaimed, status, loadKey, createAnalyticsEvent]);

	const value = useMemo<AiPlannerIframeContextValue>(
		() => ({ status, warm, prewarm, claim, retry }),
		[status, warm, prewarm, claim, retry],
	);

	/*
	 * Persistent host for the shared iframe. It NEVER changes parents (so the
	 * iframe never reloads). When claimed it is positioned (fixed) directly over
	 * the modal slot; when parked it collapses to a hidden box.
	 *
	 * It is portaled to document.body so it escapes any ancestor stacking context
	 * (a parent with transform/opacity/filter caps z-index no matter how high) —
	 * otherwise the iframe renders behind the modal blanket and the nav.
	 */
	const host = (
		<div
			ref={hostRef}
			css={[styles.host, isClaimed && hostRect != null ? styles.hostVisible : styles.hostHidden]}
			// The overlay position/size are measured from the modal slot at runtime,
			// so they are applied as dynamic inline styles — there is no static value
			// the css prop could express. Every value is a conditional (never a bare
			// literal), so this is purely a dynamic-value channel.
			// One above the ADS `modal` layer (510): the iframe is portaled to <body>
			// where it competes with the modal (also at <body>, layer 510); a z-index
			// tie lets the later-painted modal win, so 511 keeps us above the modal
			// blanket (500) and nav (200).
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				top: isClaimed && hostRect != null ? hostRect.top : undefined,
				left: isClaimed && hostRect != null ? hostRect.left : undefined,
				width: isClaimed && hostRect != null ? hostRect.width : undefined,
				height: isClaimed && hostRect != null ? hostRect.height : undefined,
				zIndex: isClaimed && hostRect != null ? 511 : undefined,
			}}
			aria-hidden={isClaimed ? undefined : 'true'}
			// Marks this host (portaled to <body>, outside the modal subtree) so the
			// modal's focusLockAllowlist excludes it — letting keyboard focus reach
			// the embedded planner iframe. See modalProps.tsx.
			{...{ [AI_PLANNER_IFRAME_HOST_ATTRIBUTE]: '' }}
		>
			{isMounted && (
				<iframe
					key={loadKey}
					ref={iframeRef}
					css={styles.iframe}
					src={src}
					title="Planner"
					// Required by the Rovo content bridge so loadPlan commands are
					// routed to the planner window. Must match the planner's child id.
					{...{ [aiPlannerReceiverIdAttribute]: getAiPlannerEmbedReceiverId() }}
					onLoad={handleLoad}
					onError={handleError}
				/>
			)}
		</div>
	);

	return (
		<AiPlannerIframeContext.Provider value={value}>
			{children}
			<RenderOnClient>
				{globalThis.document?.body != null ? createPortal(host, globalThis.document.body) : null}
			</RenderOnClient>
		</AiPlannerIframeContext.Provider>
	);
};

export const useAiPlannerIframe = (): AiPlannerIframeContextValue | null =>
	useContext(AiPlannerIframeContext);

const styles = cssMap({
	host: {
		position: 'fixed',
		overflow: 'hidden',
	},
	// Parked: collapsed, out of layout and the a11y tree, not interactive.
	hostHidden: {
		// display:none prevents unclaimed iframe host from taking focus
		display: 'none',
	},
	// Claimed: visible overlay sitting exactly over the modal slot. Position/size
	// AND z-index come from inline styles (measured from the slot; z-index 511 is
	// outside the typed token union, see the host element).
	hostVisible: {
		opacity: 1,
		pointerEvents: 'auto',
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
	},
});
